"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  buildStoryOutline,
  type LengthUnit,
  type StoryOutlineInput,
  type StoryStructure,
} from "@/lib/tool-kit/essay-templates/story-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const STORY_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  structureLabel: "Structure",
  structures: {
    "three-act": "Three-act structure",
    "heros-journey": "The Hero's Journey",
    "save-the-cat": "Save the Cat",
    freytag: "Freytag's pyramid",
  },
  structureHints: {
    "three-act":
      "Nine beats across setup, confrontation, and resolution, with the turning points near the quarter marks.",
    "heros-journey":
      "Christopher Vogler's twelve stages of the mythic journey, from the Ordinary World to the Return with the Elixir.",
    "save-the-cat":
      "Blake Snyder's fifteen beats, placed by his page numbers and scaled to your target length.",
    freytag:
      "Freytag's five parts — exposition, rising action, climax, falling action, resolution — plus the inciting incident that starts the rise.",
  },
  storyHeading: "Your story",
  titleLabel: "Working title (optional)",
  titlePlaceholder: "e.g. The Drowned Quarter",
  nameLabel: "Protagonist's name",
  namePlaceholder: "e.g. Ines Okafor",
  descriptionLabel: "Who they are, in a phrase",
  descriptionPlaceholder:
    "e.g. a junior surveyor who trusts maps more than people",
  goalLabel: "Goal — what they want",
  goalPlaceholder: "e.g. to win back the family farm",
  obstacleLabel: "Main obstacle — who or what stands in the way",
  obstaclePlaceholder: "e.g. a rival who wants the same land",
  settingLabel: "Setting — where and when",
  settingPlaceholder: "e.g. a fishing town in 1920s Maine",
  themeLabel: "Theme (optional)",
  themePlaceholder: "e.g. home is something you choose",
  lengthHeading: "Target length (optional)",
  lengthLabel: "Length",
  lengthPlaceholder: "e.g. 80000",
  lengthHint: "Places each beat at its approximate word or page.",
  unitLabel: "Unit",
  units: { words: "Words", pages: "Pages" },
  fileName: "story-outline",
  document: {
    heading: "Your story outline",
    prompt: "To write",
    guidance: "Where",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: beat to draft|other: beats to draft",
    complete: "Every beat has your content.",
  },
  defaultTitle: "Story Outline",
  subtitles: {
    "three-act": "Three-act structure: setup, confrontation, resolution",
    "heros-journey": "The Hero's Journey in Christopher Vogler's twelve stages",
    "save-the-cat": "Save the Cat beat sheet (Blake Snyder, 2005)",
    freytag: "Freytag's pyramid (Gustav Freytag, 1863)",
  },
  essentialsHeading: "Story Essentials",
  fields: {
    protagonist: "Protagonist",
    goal: "Goal",
    obstacle: "Obstacle",
    setting: "Setting",
    theme: "Theme",
  },
  protagonistValue: "{name} — {description}",
  beatHeading: "{n}. {beat}",
  acts: { one: "Act One", two: "Act Two", three: "Act Three" },
  actAndPosition: "{act} · {position}",
  positions: {
    atStart: "at the very start",
    atEnd: "at the very end",
    wordAt: "around word {n}",
    wordRange: "around words {from}–{to}",
    wordUpTo: "the first {to} words or so",
    pageAt: "around page {n}",
    pageRange: "around pages {from}–{to}",
    pageUpTo: "the first {to} pages or so",
    percentAt: "about {percent} of the way through",
    percentRange: "about {from}–{to} of the way through",
    percentUpTo: "the first {to} or so",
    snyderAt: "page {n} of Snyder's 110-page script",
    snyderRange: "pages {from}–{to} of Snyder's 110-page script",
  },
  beatNames: {
    "ta-setup": "Setup",
    "ta-inciting": "Inciting Incident",
    "ta-plot-one": "Plot Point One",
    "ta-rising": "Rising Action",
    "ta-midpoint": "Midpoint",
    "ta-complications": "Complications",
    "ta-plot-two": "Plot Point Two",
    "ta-climax": "Climax",
    "ta-resolution": "Resolution",
    "hj-ordinary-world": "The Ordinary World",
    "hj-call": "The Call to Adventure",
    "hj-refusal": "Refusal of the Call",
    "hj-mentor": "Meeting with the Mentor",
    "hj-threshold": "Crossing the First Threshold",
    "hj-tests": "Tests, Allies, Enemies",
    "hj-approach": "Approach to the Inmost Cave",
    "hj-ordeal": "The Ordeal",
    "hj-reward": "Reward (Seizing the Sword)",
    "hj-road-back": "The Road Back",
    "hj-resurrection": "The Resurrection",
    "hj-return": "Return with the Elixir",
    "stc-opening-image": "Opening Image",
    "stc-theme-stated": "Theme Stated",
    "stc-set-up": "Set-Up",
    "stc-catalyst": "Catalyst",
    "stc-debate": "Debate",
    "stc-break-into-two": "Break into Two",
    "stc-b-story": "B Story",
    "stc-fun-and-games": "Fun and Games",
    "stc-midpoint": "Midpoint",
    "stc-bad-guys": "Bad Guys Close In",
    "stc-all-is-lost": "All Is Lost",
    "stc-dark-night": "Dark Night of the Soul",
    "stc-break-into-three": "Break into Three",
    "stc-finale": "Finale",
    "stc-final-image": "Final Image",
    "fr-exposition": "Exposition",
    "fr-inciting": "Inciting Incident",
    "fr-rising": "Rising Action",
    "fr-climax": "Climax",
    "fr-falling": "Falling Action",
    "fr-resolution": "Resolution",
  },
  beatPrompts: {
    "ta-setup":
      "Introduce {name} in {setting} before anything changes. Show an ordinary day, what they care about, and the flaw or wound the story will test.",
    "ta-inciting":
      "An event disrupts {name}'s ordinary life and raises the story's central question. What happens, and why can't it be ignored? (Goal at stake: {goal}.)",
    "ta-plot-one":
      "{name} commits to the goal, and a door closes behind them. What decision launches Act Two, and why is there no going back?",
    "ta-rising":
      "{name} tries the obvious approach and runs into {obstacle}. Make each attempt cost more than the last.",
    "ta-midpoint":
      "A revelation, victory, or reversal raises the stakes. What does {name} learn that changes how they pursue the goal?",
    "ta-complications":
      "The opposition strikes back. Take away {name}'s easy options and turn allies or circumstances against them.",
    "ta-plot-two":
      "The lowest point: the goal seems lost. What must {name} face about themselves before the final push?",
    "ta-climax":
      "The decisive confrontation with {obstacle}. How does {name} use what the story has taught them to win, lose, or redefine the goal?",
    "ta-resolution":
      "Show the new normal: how has {name}'s world changed, and what does the ending say about the theme ({theme})?",
    "hj-ordinary-world":
      "Show {name}'s everyday life in {setting}, and the inner problem they carry before the adventure begins.",
    "hj-call":
      "A challenge or opportunity arrives and sets the goal ({goal}). What is it, and who or what delivers it?",
    "hj-refusal":
      "{name} hesitates or refuses. What fear, duty, or doubt holds them back?",
    "hj-mentor":
      "Someone or something gives {name} advice, training, a gift, or confidence. What do they offer, and what can't they give?",
    "hj-threshold":
      "{name} commits and enters the special world of the adventure. What marks the point of no return?",
    "hj-tests":
      "{name} learns the new world's rules, gathers allies, and makes enemies — including the first real sign of {obstacle}.",
    "hj-approach":
      "{name} and their allies prepare for the central ordeal. What plans, doubts, or setbacks come first?",
    "hj-ordeal":
      "The great crisis: {name} faces their deepest fear, or {obstacle}, head-on and seems to die — literally or figuratively.",
    "hj-reward":
      "Having survived, {name} takes the reward — an object, knowledge, or a reconciliation. What does it cost?",
    "hj-road-back":
      "{name} turns for home, but the consequences of the Ordeal follow. What forces them to recommit?",
    "hj-resurrection":
      "The final, highest-stakes test. {name} must use everything they have learned, and is transformed by it ({theme}).",
    "hj-return":
      "{name} returns to the ordinary world changed, bringing back something that helps others — the elixir. What has changed for good?",
    "stc-opening-image":
      "A single snapshot of {name}'s world before the story changes it — a scene you can mirror in the Final Image.",
    "stc-theme-stated":
      "Someone other than {name} hints at the lesson the story is about ({theme}). {name} hears it but doesn't understand it yet.",
    "stc-set-up":
      "Show {name} at home, at work, and at play in {setting}. Plant every flaw and problem that will need fixing later.",
    "stc-catalyst":
      "The event that knocks {name} out of the status quo: news, an invitation, a loss, or a discovery.",
    "stc-debate":
      "{name} hesitates. Should they act? Can they? Show the doubt, and what staying put would cost.",
    "stc-break-into-two":
      "{name} chooses to act — pursuing the goal ({goal}) — and steps into an upside-down version of their world. The choice must be theirs.",
    "stc-b-story":
      "Introduce the second storyline, often a friendship or a romance, whose characters will help {name} learn the theme.",
    "stc-fun-and-games":
      "The promise of the premise: the scenes a reader came for, as {name} explores the new world and tests themselves against {obstacle}.",
    "stc-midpoint":
      "A false victory or a false defeat. The stakes rise, time may start running out, and the main story and the B story cross.",
    "stc-bad-guys":
      "Enemies outside regroup and doubts inside spread. {name}'s team frays, and the flaws from the Set-Up come back.",
    "stc-all-is-lost":
      "The opposite of the Midpoint: the lowest moment, often marked by a loss — what Snyder calls the “whiff of death”.",
    "stc-dark-night":
      "{name} sits with the loss. In the darkness, they finally understand the theme.",
    "stc-break-into-three":
      "A new idea — from the B Story or the theme — shows {name} the way forward, and they choose to fight again.",
    "stc-finale":
      "{name} uses everything they have learned to confront {obstacle}. The flaws are fixed, and the old world is changed for good.",
    "stc-final-image":
      "The mirror of the Opening Image: show how much {name} and their world have changed.",
    "fr-exposition":
      "Introduce {name}, the setting ({setting}), and the situation before the conflict. What does the audience need to know?",
    "fr-inciting":
      "The event that sets the conflict in motion — what Freytag called the exciting force. How does it set {name} against {obstacle}?",
    "fr-rising":
      "A chain of complications raises the tension step by step as {name} pursues the goal ({goal}).",
    "fr-climax":
      "The turning point at the peak of the pyramid: the moment {name}'s fortunes change for good, for better or worse.",
    "fr-falling":
      "The consequences of the climax unfold, and the loose threads draw toward the ending.",
    "fr-resolution":
      "The conflict is resolved and a new order is set — in a tragedy, what Freytag called the catastrophe. What does the ending say about the theme ({theme})?",
  },
  fallback: {
    name: "your protagonist",
    goal: "what they want",
    obstacle: "what stands in the way",
    setting: "the story's world",
    theme: "the theme",
  },
  example: {
    title: "The Drowned Quarter",
    name: "Ines Okafor",
    description:
      "a 24-year-old junior surveyor at the city records office who trusts maps more than people",
    goal: "to prove the flooded quarter is still lived in before the council sells it to developers",
    obstacle: "the deputy mayor, who is quietly rewriting the city's records",
    setting:
      "a canal city in the late 2040s, where rising water has emptied whole districts",
    theme: "a place survives only as long as someone is willing to witness it",
    length: "90000",
  },
} as const;

const STRUCTURES: readonly StoryStructure[] = [
  "three-act",
  "heros-journey",
  "save-the-cat",
  "freytag",
];
const UNITS: readonly LengthUnit[] = ["words", "pages"];
const EMPTY: StoryOutlineInput = {
  structure: "save-the-cat",
  title: "",
  name: "",
  description: "",
  goal: "",
  obstacle: "",
  setting: "",
  theme: "",
  length: "",
  unit: "words",
};

type TextKey = Exclude<keyof StoryOutlineInput, "structure" | "unit">;

const STORY_FIELDS = [
  { key: "name", label: "nameLabel", placeholder: "namePlaceholder" },
  {
    key: "description",
    label: "descriptionLabel",
    placeholder: "descriptionPlaceholder",
  },
  { key: "goal", label: "goalLabel", placeholder: "goalPlaceholder" },
  {
    key: "obstacle",
    label: "obstacleLabel",
    placeholder: "obstaclePlaceholder",
  },
  { key: "setting", label: "settingLabel", placeholder: "settingPlaceholder" },
  { key: "theme", label: "themeLabel", placeholder: "themePlaceholder" },
] as const;

export default function StoryOutlineGeneratorWidget() {
  const strings = useToolUi(STORY_OUTLINE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<StoryOutlineInput>(EMPTY);
  const doc = useMemo(
    () => buildStoryOutline(input, strings, fmt, locale),
    [input, strings, fmt, locale]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...strings.example,
              structure: previous.structure,
              unit: "words",
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              structure: previous.structure,
            }))
          }
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <div className="space-y-2">
        <PillGroup
          label={strings.structureLabel}
          name="st-structure"
          onChange={(structure) =>
            setInput((previous) => ({ ...previous, structure }))
          }
          options={STRUCTURES.map((value) => ({
            value,
            label: strings.structures[value],
          }))}
          value={input.structure}
        />
        <p className={HINT}>{strings.structureHints[input.structure]}</p>
      </div>

      <Panel title={strings.storyHeading}>
        <TextField
          id="st-title"
          label={strings.titleLabel}
          onChange={update("title")}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {STORY_FIELDS.map((field) => (
            <TextField
              id={`st-${field.key}`}
              key={field.key}
              label={strings[field.label]}
              onChange={update(field.key)}
              placeholder={strings[field.placeholder]}
              value={input[field.key]}
            />
          ))}
        </div>
      </Panel>

      <Panel title={strings.lengthHeading}>
        <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
          <TextField
            hint={strings.lengthHint}
            id="st-length"
            label={strings.lengthLabel}
            numeric
            onChange={update("length")}
            placeholder={strings.lengthPlaceholder}
            value={input.length}
          />
          <SelectField
            id="st-unit"
            label={strings.unitLabel}
            onChange={(unit) => setInput((previous) => ({ ...previous, unit }))}
            options={UNITS.map((value) => ({
              value,
              label: strings.units[value],
            }))}
            value={input.unit}
          />
        </div>
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="st"
        labels={strings.document}
      />
    </div>
  );
}
